import React from "react"
import { useTranslation } from "react-i18next"

import { useNavigate, useParams } from "react-router-dom"
import ProtectedLayout from "layouts/Protected"
import { useAuth, useBikeStore } from "hooks"
import { isNotEmpty } from "utils/validation"

import Logo from "components/Elements/logos/Logo"
import { CaretIcon } from "components/Elements/Icons"

import {
    BIKE_BACK_STORE,
    BIKE_SOLD,
    BIKE_STORE,
    BIKE_IN_SALE,
    REQUEST_ACCEPTED,
    REQUEST_FORCED,
    REQUEST_ONGOING,
    BACK_PROPERTY_TRANSFER_REQUEST,
    BUY_PROPERTY_TRANSFER_REQUEST,
    REQUEST_REJECTED,
    bikeAccountTypes,
    UPDATE_MODE
} from "utils/constants"

// Bike forms
import { FormSearchBike, FormBikeInfos, FormSellBike, FormBikeSold, PartnerLink, BackAndBuyBike, FormBikeEvent } from "./Partials"
import { BackToStoreRequestedCard } from "./Partials/BackAndBuyBike/BackToStore/Partials"
import { BuyRequestedCard } from "./Partials/BackAndBuyBike/Buy/Partials"

export const BikePage = () => {
    const {
        currentBike: bike,
        bikeStatus,
        showOwner,
        isSeller,
        isWalletSeller,
        setShowOwner,
        fetchBike
    } = useBikeStore()

    const { slug } = useParams()
    const { t } = useTranslation()
	const navigate = useNavigate()

    const { entity } = useAuth()
    // or is the user an admin ?
    const isBO = entity?.entity_data?.entity_permission?.role === "MANAGEENTITIES"

    const [boReadOnly, setBoReadOnly] = React.useState(true)

    const goBack = () => showOwner ? setShowOwner(false) : navigate(-1)

    React.useEffect(() => {
        fetchBike(slug)
    }, []) // eslint-disable-line

    const bikeTransferRequestStatus = bike?.propertyTransferRequest?.status
    const isBikeTransferRequestConfirmed = bikeTransferRequestStatus === REQUEST_ACCEPTED || bikeTransferRequestStatus === REQUEST_FORCED
    const isBikeTransferRequestOnGoing = bikeTransferRequestStatus === REQUEST_ONGOING || bikeTransferRequestStatus === REQUEST_REJECTED
    const isBackToStoreTransferRequested = bike?.propertyTransferRequest?.context === BACK_PROPERTY_TRANSFER_REQUEST
    const isBuyTransferRequested = bike?.propertyTransferRequest?.context === BUY_PROPERTY_TRANSFER_REQUEST

    // We compare the current entity (entity_id) and the entity from the bike transfer request (store_id)
    const bikeTransferRequestStoreId = bike?.propertyTransferRequest?.store_id
    const requesterStoreId = bikeTransferRequestStoreId?.startsWith("ST-") ? bikeTransferRequestStoreId.slice(3) : bikeTransferRequestStoreId
    const isBikeTransferRequesterStore = entity.entity_id === requesterStoreId

    const isBikeTransferRequesterStoreAndOnGoing = isBikeTransferRequestOnGoing && isBikeTransferRequesterStore

    const accountType = bikeAccountTypes.find(({ suffix }) => bike?.accountType === `${bike?.operator}${suffix}`)
    const isWalletMigratedAccountType = accountType?.id !== "wallet_not_migrated" && accountType?.id !== "nowallet_not_migrated"

    const isManufacturerNFT = bike?.descriptionMadeBy === "MANUFACTURER_NFT"

    return (
        <ProtectedLayout className="px-5 p-12">
            <div className="l-container">
                <div className="c-section-title mb-7">
                    <div className="c-section-title__content justify-between">
                        <div className="flex items-center justify-start w-full">
                            <button onClick={goBack} className="flex" aria-label={t("back_to_search", { ns: "bike" })}>
                                <span className="flex py-2" style={{ transform: "rotate(180deg)" }}><CaretIcon size={30} /></span>
                            </button>
                            <h1 className="c-section-title__title c-h2 text-primary font-medium ml-5">
                                {slug ? slug : ""}
                            </h1>
                        </div>
                        <div className="flex items-center justify-end w-full">
                            <span className="uppercase">{t("identify_by", { ns: "bike" })}</span>
                            <Logo name={bike?.identifier} className="py-2 u-inline-flex" defaultColor={true} />
                        </div>
                    </div>
                </div>

                <div className="c-card p-5 md:p-12 mb-7">
                    <FormSearchBike readonly={true} isAdmin={isBO} />
                </div>

                {!bike &&
                    <div className="c-card p-5 lg:px-12 lg:py-7 mb-7">
                        <div className="flex justify-center p-12">
                            <div className="c-spinner"></div>
                        </div>
                    </div>
                }

                {/* Vue limitée pour le BO */}
                {bike && isBO &&
                    <>
                        <div className="c-card p-5 md:p-12 mb-7">
                            <FormBikeSold
                                disabled={bikeStatus !== 1}
                                isAdmin={isBO}
                            />
                        </div>

                        {isBikeTransferRequestOnGoing && isBackToStoreTransferRequested &&
                            <div className="mb-7">
                                <BackToStoreRequestedCard isBO={isBO} />
                            </div>
                        }

                        {isBikeTransferRequestOnGoing && isBuyTransferRequested &&
                            <div className="mb-7">
                                <BuyRequestedCard isBO={isBO} />
                            </div>
                        }

                        <FormBikeInfos saveMode={UPDATE_MODE} readonly={boReadOnly} toggleReadOnly={() => setBoReadOnly(s => !s)} />
                        <PartnerLink />
                    </>

                }

                {/* Vue étendue pour les vendeurs */}
                {bike && !isBO &&
                    <>
                        {/* Le vélo est en magasin */}
                        {(bikeStatus === BIKE_STORE || (bikeStatus === BIKE_BACK_STORE && isSeller) || isWalletSeller) &&
                            <>
                                {!showOwner &&
                                    <>
                                        <div className="mb-7">
                                            {/* NB: si le vélo vient du wallet ses infos ne peuvent pas être modifiées */}
                                            <FormBikeInfos readonly={bikeStatus !== BIKE_STORE && isWalletSeller} disableBikeInfos={isWalletSeller} />
                                        </div>

                                        {/* 
                                            NB: si le vélo vient du wallet et qu'il a été vendu ou qu'il est en vente, on peut faire une demande
                                            de retour rachat.
                                        */}
                                        {
                                            isWalletSeller && (bikeStatus === BIKE_SOLD || bikeStatus === BIKE_IN_SALE) &&
                                            // The seller can't see BackAndBuyBike if request is ongoing and requester is an another store 
                                            !(isBikeTransferRequestOnGoing && !isBikeTransferRequesterStore) && <BackAndBuyBike />
                                        }

                                        {/* The component is displayed if request is confirmed (validated or forced) and the store requesting */
                                            isBikeTransferRequestConfirmed && isBikeTransferRequesterStore && isBackToStoreTransferRequested && <BackToStoreRequestedCard />}

                                        {/* The component is displayed if request is confirmed and the store requesting */
                                            isBikeTransferRequestConfirmed && isBikeTransferRequesterStore && isBuyTransferRequested && <BuyRequestedCard />}
                                    </>
                                }
                                {(showOwner || isWalletSeller) &&
                                    <div className="c-card p-5 lg:px-12 lg:py-7 mb-7">
                                        <FormSellBike />
                                    </div>
                                }
                            </>
                        }


                        {/* Le vélo a été vendu par un autre magasin : le mail du propriétaire est caché */}
                        {bikeStatus !== BIKE_BACK_STORE && !isSeller && !isWalletSeller &&
                            <>
                                {isWalletMigratedAccountType && <div className="c-card p-5 md:p-12 mb-7">
                                    <FormBikeSold
                                        disabled={true}
                                        isAdmin={isBO}
                                    />
                                </div>}

                                {/* An another store can see BackAndBuyBike if no request on going or if it is the requester */
                                    (
                                        ((bikeStatus === BIKE_SOLD || bikeStatus === BIKE_IN_SALE) && !isBikeTransferRequestOnGoing) || isBikeTransferRequesterStoreAndOnGoing)
                                    && <BackAndBuyBike />
                                }

                                <FormBikeInfos readonly={true} />
                            </>
                        }

                        {/* Le vélo a été vendu par mon magasin : le mail du propriétaire est visible */}
                        {isSeller && (bikeStatus === BIKE_SOLD || bikeStatus === BIKE_IN_SALE) &&
                            <>
                                <div className="c-card p-5 md:p-12 mb-7">
                                    <FormBikeSold
                                        disabled={false}
                                        isAdmin={isBO}
                                    />
                                </div>

                                {/* The seller can't see BackAndBuyBike if request is ongoing and requester is an another store */
                                    !(isBikeTransferRequestOnGoing && !isBikeTransferRequesterStore) && <BackAndBuyBike />}

                                <FormBikeInfos readonly={true} />
                            </>
                        }
                        {(isManufacturerNFT && isNotEmpty(bike.status) && bike.status === BIKE_SOLD) && <FormBikeEvent/>}
                    </>
                }
            </div>
        </ProtectedLayout>
    )
}